/**
 * The `customers` section — RO-3-04 (task #44297).
 *
 * Scope (docs/rollover/inventory/pages.md § Customers — 4 pages):
 *   - customers/page.tsx (285 LOC, REWRITE) → `/customers`
 *   - customers/[...segments]/page.tsx (18 LOC, DELEGATOR) → `/customers/$`
 *     (replaces the RO-3-14 pending placeholder)
 *   - customers/o/[uid]/page.tsx — already ported as a pure redirect in
 *     dynamic-segments.tsx (RO-3-14); not re-registered here
 *   - market/page.tsx (212 LOC, REWRITE) → `/market` (see market.tsx)
 *
 * The mode root's server reads became contracted READs under `/api/customers/*`.
 * The five Next `app/api/customers/**` twins are mounted too so PortfolioView /
 * QuickCreateCustomer / LogActivity keep working on this host.
 *
 * Shared ModePage / ModeSectionRoute / CRM views come from RO-3-00c/d.
 * Live Next originals stay under excellent/app/ (PRD D6).
 */

import * as React from "react";
import { z } from "zod";
import { createRoute } from "@tanstack/react-router";
import { hopLeftoverLegacyRoute } from "./agents-unmount-hop";
import { List, Kanban, LineChart, Play, Layers, LayoutDashboard } from "@excellent/ui/icons";
import { useApi } from "@excellent/ui/api";
import { customers } from "@excellent/ui/api/contracts/customers";
import { ForecastView } from "@excellent/ui/blocks/crm/forecast-view";
import { PortfolioView } from "@excellent/ui/blocks/crm/portfolio-view";
import type { ForecastModel } from "@excellent/core";
import type { ScopedTask } from "@excellent/ui/types/projects";
import { useNavigate, useSearch } from "@excellent/ui/router";
import { MODE_VIEWS, type ModeView } from "@/modes/modes-registry";
import { rootRoute } from "./__root";
import { routeSearchSchema } from "../shell/search";
import { ModePage } from "../shared/modes/mode-page";
import {
  ModeSectionRoute,
  redirectAreaToPath,
} from "../shared/modes/mode-section-route";
import { CustomersDashboard } from "../shared/customers/customers-dashboard";
import { CustomersColdStart } from "../shared/customers/customers-cold-start";
import { CustomerPropertyFacet } from "../shared/customers/property-facet";
import { PortfolioBoard } from "../shared/customers/portfolio-board";

/** `?pipeline=` is route-local (board switcher only). Shell owns view/area/sub/q. */
const customersSearch = routeSearchSchema({
  pipeline: z.string().optional(),
});

function CustomersModePage() {
  const navigate = useNavigate();
  // Seam useSearch (URLSearchParams), not customersRoute.useSearch():
  // RO-10-08 / #44581 empty-splat handoff renders this from
  // customersSectionsRoute, and a route-scoped useSearch throws
  // ("Invariant failed") when that route is not the match — same trap as
  // product.tsx / work.tsx.
  const spParams = useSearch();
  const view = spParams.get("view") ?? undefined;
  const area = spParams.get("area") ?? undefined;
  const sub = spParams.get("sub") ?? undefined;
  const pipeline = spParams.get("pipeline") ?? undefined;
  const q = spParams.get("q") ?? undefined;
  const search: {
    view?: string;
    area?: string;
    sub?: string;
    pipeline?: string;
    q?: string;
  } = {
    ...(view ? { view } : {}),
    ...(area ? { area } : {}),
    ...(sub ? { sub } : {}),
    ...(pipeline ? { pipeline } : {}),
    ...(q ? { q } : {}),
  };

  React.useEffect(() => {
    if (search.view === "setup") {
      const anchor: Record<string, string> = {
        sequences: "sequences",
        templates: "email-templates",
        import: "import",
      };
      const frag = search.sub && anchor[search.sub] ? `#${anchor[search.sub]}` : "";
      navigate.replace(`/settings/customers${frag}`);
      return;
    }
    if (search.view === "pulse") {
      navigate.replace("/customers");
      return;
    }
    redirectAreaToPath(
      "customers",
      search as Record<string, string | undefined>,
      navigate,
    );
  }, [search.view, search.area, search.sub, search, navigate]);

  const v = (MODE_VIEWS as readonly string[]).includes(view ?? "")
    ? (view as ModeView)
    : "dashboard";

  const { data: dash, isLoading: dashLoading } = useApi(customers.dashboard, {});
  const started = dash?.started ?? false;
  const showColdStart = !started && !area && !dashLoading && dash != null;

  const viewMode = view ?? "dashboard";
  const isDashboard = viewMode === "dashboard" && !showColdStart;
  const isForecast = viewMode === "forecast" && !showColdStart;
  const isProperties = viewMode === "properties" && !showColdStart;
  const isBoard = viewMode === "board" && !showColdStart;

  const { data: forecastData } = useApi(customers.forecast, isForecast ? {} : null);
  const { data: facetsData } = useApi(
    customers.propertyFacets,
    isProperties ? {} : null,
  );

  const cutTabs = [
    { href: "/customers", label: "Overview", icon: <LayoutDashboard className="size-4" /> },
    { href: "/customers?view=list", label: "List", icon: <List className="size-4" /> },
    { href: "/customers?view=board", label: "Pipeline", icon: <Kanban className="size-4" /> },
    { href: "/customers?view=forecast", label: "Forecast", icon: <LineChart className="size-4" /> },
    { href: "/customers?view=properties", label: "Properties", icon: <Layers className="size-4" /> },
    { href: "/customers?view=queue", label: "Queue", icon: <Play className="size-4" /> },
  ];

  const noun = dash?.noun ?? { singular: "Customer", plural: "Customers" };

  let cutBody: React.ReactNode = null;
  if (isDashboard && dash) {
    cutBody = (
      <CustomersDashboard
        rows={dash.rows}
        metrics={dash.metrics}
        urgent={dash.urgent}
        tasks={dash.tasks as unknown as ScopedTask[]}
        newDealHref={dash.newDealHref}
        noun={noun}
      />
    );
  } else if (isBoard) {
    cutBody = <PortfolioBoard pipelineSlug={pipeline ?? null} />;
  } else if (isProperties) {
    cutBody = facetsData ? <CustomerPropertyFacet data={facetsData} /> : null;
  } else if (isForecast) {
    cutBody =
      forecastData?.model != null ? (
        <ForecastView model={forecastData.model as unknown as ForecastModel} />
      ) : null;
  } else if (!showColdStart) {
    cutBody = <PortfolioView noun={noun} />;
  }

  const dashboardSlot = showColdStart ? (
    <CustomersColdStart noun={noun} />
  ) : (
    cutBody
  );

  return (
    <ModePage
      mode="customers"
      view={showColdStart ? "dashboard" : v}
      area={area}
      sub={sub}
      searchParams={search as Record<string, string | undefined>}
      dashboardSlot={dashboardSlot}
      titleTabs={showColdStart ? undefined : cutTabs}
      hideHeader={showColdStart}
    />
  );
}

export const customersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/customers",
  validateSearch: customersSearch,
  beforeLoad: ({ location }) => hopLeftoverLegacyRoute(location),
  component: CustomersModePage,
});

export const customersSectionsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/customers/$",
  // #44601: empty splat outranks bare `/customers` — reject so the index wins
  // the match (render hand-off below stays as belt-and-suspenders).
  params: {
    parse: (raw) => {
      if (!raw._splat) return false;
      return raw;
    },
  },
  validateSearch: customersSearch,
  beforeLoad: ({ location }) => hopLeftoverLegacyRoute(location),
  component: function CustomersSectionsPage() {
    const search = customersSectionsRoute.useSearch() as unknown as Record<
      string,
      string | string[] | undefined
    >;
    const { _splat } = customersSectionsRoute.useParams();
    // RO-10-08 / #44581: empty splat steals bare `/customers` from
    // customersRoute — hand off so the mode root still paints (h1 "Customers").
    // Same trap as product.tsx / work.tsx; ModeSectionRoute with zero segments
    // returns null and the page is blank.
    const splat = _splat ?? "";
    if (!splat) return <CustomersModePage />;
    const segments = splat.split("/").filter(Boolean);
    return (
      <ModeSectionRoute
        mode="customers"
        segments={segments}
        searchParams={search}
      />
    );
  },
});
